/**
 * Settings · Fonts — pick the app typeface or reset to the bundled brand.
 *
 * The brand font (Nunito) is bundled and self-contained, so it is ALWAYS the
 * default and "Reset to default" always has a target — even with the catalog
 * unavailable. Picking a catalog font sets the `--font-user` override live; Reset
 * clears it. The preference persists per-device (localStorage), guest-first.
 */

import { For, Show } from "solid-js";
import { createRoute, useNavigate } from "@tanstack/solid-router";
import { ArrowLeft, Check, RotateCcw, Type } from "@fleet/icons";
import { msg } from "@lingui/core/macro";

import { rootRoute } from "@/routes/__root";
import { useTypography } from "@/providers/typography-provider";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { GlassSurface } from "@/components/ui/glass-surface";
import { Atmosphere } from "@/components/ui/atmosphere";

function SettingsFontsComponent() {
  const typography = useTypography();
  const { t } = useI18n();
  const navigate = useNavigate();

  const isBrand = () => typography.selectedFontId() === null;
  const currentLabel = () => typography.fontFamilyName() ?? typography.brandLabel;

  // The chosen-but-gone font notice (plain locals so lingui can extract the message).
  const unavailableNotice = () => {
    const gone = typography.unavailableFontName() ?? "";
    const brand = typography.brandLabel;
    return t(
      msg`The font “${gone}” is no longer available, so the app default (${brand}) is applied. Pick another font below, or keep the default.`,
    );
  };

  return (
    <div class="relative min-h-dvh w-full">
      <Atmosphere />
      <div class="mx-auto flex min-h-dvh w-full max-w-2xl flex-col gap-5 p-4 sm:p-6">
        <header class="flex items-center gap-3 pt-6 pb-2">
          <Button
            variant="ghost"
            size="icon"
            aria-label={t(msg`Back`)}
            onClick={() => {
              void navigate({ to: "/" });
            }}
          >
            <ArrowLeft class="h-5 w-5" />
          </Button>
          <div class="flex items-center gap-2">
            <Type class="h-5 w-5 text-primary" />
            <h1 class="text-lg font-semibold tracking-tight">{t(msg`Typeface`)}</h1>
          </div>
        </header>

        <GlassSurface class="flex flex-col gap-3 p-4">
          <div class="flex items-center justify-between gap-3">
            <div class="flex flex-col">
              <span class="text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">
                {t(msg`Current`)}
              </span>
              <span data-testid="font-current-label" class="text-sm font-medium text-foreground">
                {/* oxlint-disable-next-line lingui/no-expression-in-message -- id already extracted; deferred extract */}
                <Show when={!isBrand()} fallback={t(msg`${currentLabel()} (app default)`)}>
                  {currentLabel()}
                </Show>
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              class="gap-2"
              data-testid="font-reset-button"
              disabled={isBrand()}
              onClick={() => {
                typography.resetFont();
              }}
            >
              <RotateCcw class="h-4 w-4" />
              {t(msg`Reset to default`)}
            </Button>
          </div>
          <p class="text-xs text-muted-foreground">
            {t(
              msg`Susurro ships with its own bundled font, so it always renders — even offline. Optional fonts below are downloaded only when you pick them.`,
            )}
          </p>
        </GlassSurface>

        <Show when={typography.isSelectedUnavailable()}>
          <div
            data-testid="font-unavailable-notice"
            role="status"
            class="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-xs text-foreground"
          >
            {unavailableNotice()}
          </div>
        </Show>

        <div class="flex flex-col gap-2">
          {/* The bundled brand default as a first-class selectable option. */}
          <button
            type="button"
            data-testid="font-default-card"
            // oxlint-disable-next-line lingui/no-expression-in-message -- id already extracted; extraction deferred
            aria-label={t(msg`Use the app default font ${typography.brandLabel}`)}
            aria-pressed={isBrand()}
            onClick={() => {
              // No-op when the brand is already the user's actual choice. When a
              // chosen font became unavailable the EFFECTIVE id is also null, but
              // there IS a stale cached choice — clicking the default is the user
              // action that persists "keep the default" and clears the notice.
              if (!isBrand() || typography.isSelectedUnavailable()) {
                typography.resetFont();
              }
            }}
            class="flex items-center justify-between gap-3 rounded-xl border border-[var(--glass-border)] bg-[var(--glass)] px-4 py-3 text-left backdrop-blur transition-colors hover:border-primary/50 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span class="flex flex-col">
              <span
                class="text-sm font-medium text-foreground"
                style={{ "font-family": "var(--font-brand)" }}
              >
                {typography.brandLabel}
              </span>
              <span class="text-xs text-muted-foreground">{t(msg`App default`)}</span>
            </span>
            <Show when={isBrand()}>
              <Check class="h-4 w-4 text-primary" />
            </Show>
          </button>
          <For each={typography.catalog}>
            {(font) => {
              const selected = () => typography.selectedFontId() === font.id;
              return (
                <button
                  type="button"
                  // oxlint-disable-next-line lingui/no-expression-in-message -- id already extracted; extraction deferred
                  aria-label={t(msg`Apply font ${font.name}`)}
                  aria-pressed={selected()}
                  onClick={() => {
                    typography.setFont({ id: font.id, name: font.name });
                  }}
                  class="flex items-center justify-between gap-3 rounded-xl border border-[var(--glass-border)] bg-[var(--glass)] px-4 py-3 text-left backdrop-blur transition-colors hover:border-primary/50 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span class="flex flex-col">
                    <span
                      class="text-sm font-medium text-foreground"
                      style={{
                        "font-family": `'${font.name}', ui-sans-serif, system-ui, sans-serif`,
                      }}
                    >
                      {font.name}
                    </span>
                    <span class="text-xs text-muted-foreground">{font.category}</span>
                  </span>
                  <Show when={selected()}>
                    <Check class="h-4 w-4 text-primary" />
                  </Show>
                </button>
              );
            }}
          </For>
        </div>
      </div>
    </div>
  );
}

export const settingsFontsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/fonts",
  component: SettingsFontsComponent,
});
